import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as requestApi from '../api/requestApi';

interface KeyRequestState {
  data: any[];
  basketCount: number;
  submittingCount: number;
  totalRowCount: number;
}

export const deleteAccountDetail = createAsyncThunk(
  'basket/deleteAccountDetail',
  async ({ requestId, id }: { requestId: any; id: any }, { dispatch }) => {
    await requestApi.deleteAccountDetail(requestId, id);

    dispatch(deleteAccountDetails({ requestId }));

    return { requestId, id };
  }
);

export const copyRequestGroup = createAsyncThunk(
  'basket/copyRequestGroup',
  async ({ id, copyDetails }: { id: any; copyDetails: any }, { dispatch }) => {
    const savedRequestGroup = (await requestApi.copyRequestGroup(
      id,
      copyDetails
    )) as { requests: unknown[] };

    savedRequestGroup.requests.forEach((savedRequest: any) => {
      dispatch(createKeyRequest());
    });

    return savedRequestGroup;
  }
);

export const saveAccountDetails = createAsyncThunk(
  'basket/saveAccountDetails',
  async ({ id, record }: { id: any; record: any }, { dispatch }) => {
    const accountDetails = (await requestApi.saveAccountDetails(
      id,
      record
    )) as any;

    dispatch(
      updateAccountDetails({
        requestId: id,
        accountDetails,
      })
    );

    return accountDetails;
  }
);

export const uploadAccountDetails = createAsyncThunk(
  'basket/uploadAccountDetails',
  async ({ id, file }: { id: any; file: any }, { dispatch }) => {
    const accountDetails = (await requestApi.uploadAccountDetails(id, {
      file,
    })) as any;

    dispatch(
      updateAccountDetails({
        requestId: id,
        accountDetails,
      })
    );

    return accountDetails;
  }
);

export const getBasket = createAsyncThunk(
  'basket/getBasket',
  async (_, { dispatch }) => {
    const requests = await requestApi.getBasket();

    dispatch(loadKeyRequests({ requests: requests as any[] }));

    return requests as any[];
  }
);

export const deleteRequest = createAsyncThunk(
  'basket/deleteRequest',
  async (
    { requestGroupId, id }: { requestGroupId: any; id: any },
    { dispatch }
  ) => {
    await requestApi.deleteRequest(requestGroupId, id);

    dispatch(
      deleteKeyRequest({
        keyRequestGroupId: requestGroupId,
        keyRequestId: id,
      })
    );

    return { requestGroupId, keyRequestId: id }; // Return both IDs for reference
  }
);

export const submitRequestGroup = createAsyncThunk(
  'basket/submitRequestGroup',
  async ({ id, record }: { id: any; record: any }, { dispatch }) => {
    await requestApi.submitRequestGroup(id, record);

    // Deconstruct the file object, as we don't need to track it
    const { file, ...updatedRecord } = record;

    const submittingRecord = { ...updatedRecord, submitting: true };

    dispatch(updateKeyRequest({ record: submittingRecord }));

    return submittingRecord;
  }
);

export const deleteRequestGroup = createAsyncThunk(
  'basket/deleteRequestGroup',
  async (id: any, { dispatch }) => {
    await requestApi.deleteRequestGroup(id);

    dispatch(deleteKeyRequestGroup({ keyRequestGroupId: id }));

    return id;
  }
);

export const bulkSaveRequests = createAsyncThunk(
  'basket/bulkSaveRequests',
  async (
    request: { companyId: string; keyWorkflowId: string; items: any[] },
    { dispatch }
  ) => {
    const savedRequestGroup = (await requestApi.bulkSaveRequests(
      request
    )) as { requests: unknown[] };

    savedRequestGroup.requests.forEach((savedRequest: any) => {
      dispatch(createKeyRequest());
    });

    return savedRequestGroup;
  }
);

export const saveRequest = createAsyncThunk(
  'basket/saveRequest',
  async (request: any, { dispatch }) => {
    const savedRequest = await requestApi.saveRequest(request);

    if (request.id) {
      dispatch(updateKeyRequest({ record: savedRequest }));
    } else {
      dispatch(createKeyRequest());
    }

    return savedRequest;
  }
);

const keyRequestSlice = createSlice({
  name: 'basket',
  initialState: null as KeyRequestState | null,
  reducers: {
    createKeyRequest: () => {
      return null; //force a reload, maybe should map it to the correct shape?
    },
    updateKeyRequest: (state, action: PayloadAction<any>) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data.map((keyRequestGroup: any) =>
          keyRequestGroup.id === action.payload.record.id
            ? action.payload.record
            : keyRequestGroup
        ),
        basketCount: action.payload.record.submitting
          ? state.basketCount - 1
          : state.basketCount,
        submittingCount: action.payload.record.submitting
          ? state.submittingCount + 1
          : state.submittingCount,
      };
    },
    keyRequestSubmitted: (
      state,
      action: PayloadAction<{ keyRequestGroupId: string }>
    ) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data.filter(
          (keyRequestGroup: any) =>
            keyRequestGroup.id !== action.payload.keyRequestGroupId
        ),
        totalRowCount:
          state.totalRowCount -
          state.data.find((x: any) => x.id === action.payload.keyRequestGroupId)
            ?.games.length,
        submittingCount: state.submittingCount - 1,
      };
    },
    deleteKeyRequest: (
      state,
      action: PayloadAction<{ keyRequestGroupId: string; keyRequestId: string }>
    ) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data
          .filter(
            (keyRequestGroup: any) =>
              keyRequestGroup.id !== action.payload.keyRequestGroupId ||
              keyRequestGroup.games.length !== 1 ||
              !keyRequestGroup.games.find(
                (x: any) => x.requestId === action.payload.keyRequestId
              )
          )
          .map((group: any) => ({
            ...group,
            games: group.games.filter(
              (game: any) => game.requestId !== action.payload.keyRequestId
            ),
          })),
        totalRowCount: state.totalRowCount - 1,
      };
    },
    deleteKeyRequestGroup: (
      state,
      action: PayloadAction<{ keyRequestGroupId: string }>
    ) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data.filter(
          (keyRequestGroup: any) =>
            keyRequestGroup.id !== action.payload.keyRequestGroupId
        ),
        totalRowCount: state.totalRowCount - 1,
        basketCount: state.basketCount - 1,
      };
    },
    loadKeyRequests: (state, action: PayloadAction<any>) => {
      return {
        ...action.payload.requests,
        basketCount: action.payload.requests.data.filter(
          (x: any) => !x.submitting
        ).length,
        submittingCount: action.payload.requests.data.filter(
          (x: any) => x.submitting
        ).length,
      };
    },
    updateAccountDetails: (
      state,
      action: PayloadAction<{ requestId: string; accountDetails: any[] }>
    ) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data.map((group: any) => ({
          ...group,
          games: group.games.map((game: any) =>
            game.requestId === action.payload.requestId
              ? {
                  ...game,
                  accountDetails:
                    game.accountDetails + action.payload.accountDetails.length,
                }
              : game
          ),
        })),
      };
    },
    deleteAccountDetails: (
      state,
      action: PayloadAction<{ requestId: string }>
    ) => {
      if (!state) return state;

      return {
        ...state,
        data: state.data.map((group: any) => ({
          ...group,
          games: group.games.map((game: any) =>
            game.requestId === action.payload.requestId
              ? {
                  ...game,
                  accountDetails: game.accountDetails - 1,
                }
              : game
          ),
        })),
      };
    },
  },
});

export const {
  createKeyRequest,
  updateKeyRequest,
  keyRequestSubmitted,
  deleteKeyRequest,
  deleteKeyRequestGroup,
  loadKeyRequests,
  updateAccountDetails,
  deleteAccountDetails,
} = keyRequestSlice.actions;

export default keyRequestSlice.reducer;
